import { ArrowLeftIcon, Button, XIcon } from '@tovala/component-library'
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react'
import { ReactNode } from 'react'
import { clsx } from 'clsx'

const Modal = ({
  children,
  onCloseModal,
}: {
  children: ReactNode
  onCloseModal: () => void
}): JSX.Element => {
  return (
    <Dialog
      onClose={() => {
        onCloseModal()
      }}
      open={true}
    >
      <div
        className="fixed inset-0 z-30 h-full max-h-full w-full bg-black opacity-40"
        onClick={onCloseModal}
      />
      <div className="fixed inset-0 z-30 flex h-full max-h-screen w-full items-center justify-center overflow-auto md:items-end">
        {children}
      </div>
    </Dialog>
  )
}

export default Modal

export const ModalBody = ({
  children,
}: {
  children: ReactNode
}): JSX.Element => {
  return <div>{children}</div>
}

export const ModalHeader = ({
  children,
  onClickBack,
  onClickClose,
}: {
  children: ReactNode
  onClickBack?: () => void
  onClickClose?: () => void
}): JSX.Element => {
  return (
    <div className="relative border-b border-grey-3 px-12 py-6">
      {onClickBack && (
        <div className="absolute left-4 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center">
          <Button buttonStyle="link" onClick={onClickBack} size="auto">
            <div className="h-6 w-6">
              <ArrowLeftIcon />
            </div>
          </Button>
        </div>
      )}
      <DialogTitle className="text-center text-k/16_125 uppercase">
        {children}
      </DialogTitle>
      {onClickClose && (
        <div className="absolute right-4 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center">
          <Button
            aria-label="Close"
            buttonStyle="link"
            onClick={onClickClose}
            size="auto"
          >
            <div className="h-6 w-6">
              <XIcon />
            </div>
          </Button>
        </div>
      )}
    </div>
  )
}

export type ModalPanelColor = 'grey' | 'white'
export type ModalPanelStyle = 'default' | 'fullHeight'

const MODAL_PANEL_STYLE_MAP: Record<ModalPanelStyle, string> = {
  default: 'max-h-full overflow-y-auto rounded-2xl md:m-1',
  fullHeight: 'h-full overflow-y-auto rounded-2xl md:m-1',
}

const MODAL_PANEL_COLOR_MAP: Record<ModalPanelColor, string> = {
  grey: 'bg-grey-0',
  white: 'bg-white',
}

export const ModalPanel = ({
  children,
  panelStyle = 'default',
  panelColor = 'white',
}: {
  children: ReactNode
  panelStyle?: ModalPanelStyle
  panelColor?: ModalPanelColor
}): JSX.Element => {
  return (
    <DialogPanel
      className={clsx(
        MODAL_PANEL_STYLE_MAP[panelStyle],
        MODAL_PANEL_COLOR_MAP[panelColor],
      )}
    >
      {children}
    </DialogPanel>
  )
}
